const DATABASE_NAME = "liuzhu-capture-v1";
const DATABASE_VERSION = 1;
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;
const RECEIPT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function localError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction) {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });
}

export function openCaptureDatabase(indexedDBImpl = globalThis.indexedDB) {
  if (!indexedDBImpl?.open) {
    throw localError("CAPTURE_INDEXEDDB_UNAVAILABLE", "IndexedDB is unavailable.");
  }
  return new Promise((resolve, reject) => {
    const request = indexedDBImpl.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains("captures")) {
        database.createObjectStore("captures", { keyPath: "captureId" });
      }
      if (!database.objectStoreNames.contains("epochs")) {
        const epochs = database.createObjectStore("epochs", {
          keyPath: ["captureId", "epochId"]
        });
        epochs.createIndex("byCapture", "captureId", { unique: false });
      }
      if (!database.objectStoreNames.contains("chunks")) {
        const chunks = database.createObjectStore("chunks", {
          keyPath: ["captureId", "epochId", "sequence"]
        });
        chunks.createIndex("byCapture", "captureId", { unique: false });
        chunks.createIndex("byCaptureState", ["captureId", "state"], {
          unique: false
        });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(
      localError("CAPTURE_INDEXEDDB_BLOCKED", "IndexedDB upgrade is blocked.")
    );
  });
}

export class CaptureStore {
  constructor({ indexedDBImpl = globalThis.indexedDB } = {}) {
    this.indexedDBImpl = indexedDBImpl;
    this.database = null;
  }

  async open() {
    if (!this.database) {
      this.database = await openCaptureDatabase(this.indexedDBImpl);
    }
    return this;
  }

  close() {
    this.database?.close();
    this.database = null;
  }

  requireOpen() {
    if (!this.database) {
      throw localError("CAPTURE_STORE_CLOSED", "Capture store is not open.");
    }
    return this.database;
  }

  async putCapture(capture) {
    const database = this.requireOpen();
    const transaction = database.transaction("captures", "readwrite");
    const store = transaction.objectStore("captures");
    const existing = await requestResult(store.get(capture.captureId));
    store.put({
      ...existing,
      captureId: capture.captureId,
      state: capture.state || existing?.state || "active",
      pendingBytes: Number(existing?.pendingBytes) || 0,
      maxPendingBytes: capture.maxPendingBytes,
      updatedAt: capture.updatedAt || Date.now(),
      createdAt: existing?.createdAt || capture.createdAt || Date.now()
    });
    await transactionDone(transaction);
  }

  async getCapture(captureId) {
    const database = this.requireOpen();
    const transaction = database.transaction("captures", "readonly");
    const result = await requestResult(
      transaction.objectStore("captures").get(captureId)
    );
    await transactionDone(transaction);
    return result || null;
  }

  async putEpoch(epoch) {
    const database = this.requireOpen();
    const transaction = database.transaction("epochs", "readwrite");
    const store = transaction.objectStore("epochs");
    const key = [epoch.captureId, epoch.epochId];
    const existing = await requestResult(store.get(key));
    store.put({
      ...existing,
      ...epoch,
      state: epoch.state || existing?.state || "active",
      lastSequence: Number.isInteger(epoch.lastSequence)
        ? epoch.lastSequence
        : existing?.lastSequence ?? -1,
      lastEndMs: Number.isInteger(epoch.lastEndMs)
        ? epoch.lastEndMs
        : existing?.lastEndMs ?? 0,
      gaps: Array.isArray(epoch.gaps)
        ? epoch.gaps.slice(0, 32)
        : existing?.gaps || [],
      updatedAt: epoch.updatedAt || Date.now(),
      createdAt: existing?.createdAt || epoch.createdAt || Date.now()
    });
    await transactionDone(transaction);
  }

  async listEpochs(captureId) {
    const database = this.requireOpen();
    const transaction = database.transaction("epochs", "readonly");
    const all = await requestResult(transaction.objectStore("epochs").getAll());
    await transactionDone(transaction);
    return all
      .filter((epoch) => epoch.captureId === captureId)
      .sort((left, right) => left.startedWallMs - right.startedWallMs);
  }

  async addGap(captureId, epochId, gap) {
    const epochs = await this.listEpochs(captureId);
    const epoch = epochs.find((item) => item.epochId === epochId);
    if (!epoch) return;
    const gaps = [...(epoch.gaps || []), gap].slice(-32);
    await this.putEpoch({ ...epoch, gaps, updatedAt: Date.now() });
  }

  async putPendingChunk(chunk, maxPendingBytes) {
    const database = this.requireOpen();
    const transaction = database.transaction(
      ["captures", "epochs", "chunks"],
      "readwrite"
    );
    const captures = transaction.objectStore("captures");
    const epochs = transaction.objectStore("epochs");
    const chunks = transaction.objectStore("chunks");
    const key = [chunk.captureId, chunk.epochId, chunk.sequence];
    const existing = await requestResult(chunks.get(key));
    if (existing) {
      if (
        existing.sha256 !== chunk.sha256 ||
        Number(existing.byteSize) !== Number(chunk.byteSize)
      ) {
        transaction.abort();
        throw localError(
          "CAPTURE_LOCAL_CHUNK_CONFLICT",
          "Local capture sequence conflicts."
        );
      }
      await transactionDone(transaction).catch(() => undefined);
      return existing;
    }
    const capture = await requestResult(captures.get(chunk.captureId));
    if (!capture) {
      transaction.abort();
      throw localError("CAPTURE_LOCAL_SESSION_MISSING", "Local capture is missing.");
    }
    const pendingBytes = Number(capture.pendingBytes) || 0;
    if (pendingBytes + chunk.byteSize > maxPendingBytes) {
      transaction.abort();
      throw localError(
        "CAPTURE_LOCAL_LIMIT",
        "Local pending capture limit was reached."
      );
    }
    chunks.put({
      ...chunk,
      state: "pending",
      createdAt: chunk.createdAt || Date.now(),
      updatedAt: Date.now()
    });
    const epochKey = [chunk.captureId, chunk.epochId];
    const epoch = await requestResult(epochs.get(epochKey));
    if (epoch) {
      epochs.put({
        ...epoch,
        lastSequence: Math.max(epoch.lastSequence ?? -1, chunk.sequence),
        lastEndMs: Math.max(epoch.lastEndMs ?? 0, chunk.endMs),
        updatedAt: Date.now()
      });
    }
    captures.put({
      ...capture,
      pendingBytes: pendingBytes + chunk.byteSize,
      updatedAt: Date.now()
    });
    await transactionDone(transaction);
    return chunk;
  }

  async listChunks(captureId) {
    const database = this.requireOpen();
    const transaction = database.transaction("chunks", "readonly");
    const all = await requestResult(transaction.objectStore("chunks").getAll());
    await transactionDone(transaction);
    return all
      .filter((chunk) => chunk.captureId === captureId)
      .sort((left, right) => {
        if (left.epochId !== right.epochId) {
          return left.epochId.localeCompare(right.epochId);
        }
        return left.sequence - right.sequence;
      });
  }

  async listPending(captureId) {
    return (await this.listChunks(captureId)).filter(
      (chunk) => chunk.state === "pending" && chunk.blob
    );
  }

  async markUploaded(captureId, epochId, sequence, receipt = {}) {
    const database = this.requireOpen();
    const transaction = database.transaction(
      ["captures", "chunks"],
      "readwrite"
    );
    const captures = transaction.objectStore("captures");
    const chunks = transaction.objectStore("chunks");
    const key = [captureId, epochId, sequence];
    const chunk = await requestResult(chunks.get(key));
    if (!chunk) {
      await transactionDone(transaction);
      return;
    }
    if (chunk.state !== "uploaded") {
      const capture = await requestResult(captures.get(captureId));
      if (capture) {
        captures.put({
          ...capture,
          pendingBytes: Math.max(
            0,
            (Number(capture.pendingBytes) || 0) - Number(chunk.byteSize || 0)
          ),
          updatedAt: Date.now()
        });
      }
    }
    chunks.put({
      ...chunk,
      blob: null,
      state: "uploaded",
      receipt: {
        byteSize: receipt.byteSize,
        sha256: receipt.sha256,
        idempotent: receipt.idempotent === true
      },
      uploadedAt: Date.now(),
      updatedAt: Date.now()
    });
    await transactionDone(transaction);
  }

  async sealEpoch(captureId, epochId) {
    const epochs = await this.listEpochs(captureId);
    const epoch = epochs.find((item) => item.epochId === epochId);
    if (!epoch) return;
    await this.putEpoch({ ...epoch, state: "sealed", updatedAt: Date.now() });
  }

  async deletePending(captureId) {
    const database = this.requireOpen();
    const transaction = database.transaction(
      ["captures", "chunks"],
      "readwrite"
    );
    const captures = transaction.objectStore("captures");
    const chunks = transaction.objectStore("chunks");
    const all = await requestResult(chunks.getAll());
    for (const chunk of all) {
      if (chunk.captureId === captureId && chunk.state === "pending") {
        chunks.delete([chunk.captureId, chunk.epochId, chunk.sequence]);
      }
    }
    const capture = await requestResult(captures.get(captureId));
    if (capture) {
      captures.put({ ...capture, pendingBytes: 0, updatedAt: Date.now() });
    }
    await transactionDone(transaction);
  }

  async prune(now = Date.now()) {
    const database = this.requireOpen();
    const transaction = database.transaction(
      ["captures", "epochs", "chunks"],
      "readwrite"
    );
    const captures = transaction.objectStore("captures");
    const epochs = transaction.objectStore("epochs");
    const chunks = transaction.objectStore("chunks");
    const all = await requestResult(chunks.getAll());
    const allEpochs = await requestResult(epochs.getAll());
    const allCaptures = await requestResult(captures.getAll());
    const removedPendingByCapture = new Map();
    const retainedCaptureIds = new Set();
    const retainedEpochKeys = new Set();
    let deleted = 0;
    for (const chunk of all) {
      const cutoff = chunk.state === "pending"
        ? now - PENDING_TTL_MS
        : now - RECEIPT_TTL_MS;
      if (Number(chunk.updatedAt || chunk.createdAt || 0) < cutoff) {
        chunks.delete([chunk.captureId, chunk.epochId, chunk.sequence]);
        if (chunk.state === "pending") {
          removedPendingByCapture.set(
            chunk.captureId,
            (removedPendingByCapture.get(chunk.captureId) || 0) +
              Number(chunk.byteSize || 0)
          );
        }
        deleted += 1;
      } else {
        retainedCaptureIds.add(chunk.captureId);
        retainedEpochKeys.add(`${chunk.captureId}:${chunk.epochId}`);
      }
    }
    for (const [captureId, removedBytes] of removedPendingByCapture) {
      const capture = await requestResult(captures.get(captureId));
      if (capture) {
        captures.put({
          ...capture,
          pendingBytes: Math.max(
            0,
            Number(capture.pendingBytes || 0) - removedBytes
          )
        });
      }
    }
    const receiptCutoff = now - RECEIPT_TTL_MS;
    for (const epoch of allEpochs) {
      const updatedAt = Number(epoch.updatedAt || epoch.createdAt || 0);
      if (
        updatedAt < receiptCutoff &&
        !retainedEpochKeys.has(`${epoch.captureId}:${epoch.epochId}`)
      ) {
        epochs.delete([epoch.captureId, epoch.epochId]);
      } else {
        retainedCaptureIds.add(epoch.captureId);
      }
    }
    for (const capture of allCaptures) {
      const updatedAt = Number(capture.updatedAt || capture.createdAt || 0);
      if (
        updatedAt < receiptCutoff &&
        !retainedCaptureIds.has(capture.captureId) &&
        !removedPendingByCapture.has(capture.captureId)
      ) {
        captures.delete(capture.captureId);
      }
    }
    await transactionDone(transaction);
    return deleted;
  }
}

export function calculateCaptureBudget({
  configuredMaxBytes,
  quota,
  usage
}) {
  const configured = Math.max(0, Number(configuredMaxBytes) || 0);
  if (!Number.isFinite(quota) || !Number.isFinite(usage) || quota <= usage) {
    return configured;
  }
  const available = quota - usage;
  const reserve = Math.max(16 * 1024 * 1024, Math.floor(available * 0.2));
  return Math.max(0, Math.min(configured, available - reserve));
}
